import React from "react";
import { useTranslation } from "react-i18next";
import sortBy from "lodash.sortby";
import {
  HvGrid,
  HvInput,
  HvDropdown,
  ListValueProp,
} from "@hitachivantara/uikit-react-core";
import { useGroups, GroupsDomain } from "lib/api/groups";
import useStyles from "./styles";

interface TableControls {
  viewOption?: "all" | "member" | "default";
  selectedFilters?: ListValueProp[];
  disableViewFilter?: boolean;
  onSearchKeyChange?: (value: string) => void;
  onViewChange?: (value: ListValueProp) => void;
  onGroupFilterChange?: (value: ListValueProp[]) => void;
}

type TableControlProps = TableControls;

const isSelected = (
  group: GroupsDomain,
  selectedFilters: ListValueProp[]
): boolean => {
  return selectedFilters.findIndex((filter) => filter.id === group.id) !== -1;
};

const generateGroupValues = (
  defaultGroups: GroupsDomain[],
  adminGroups: GroupsDomain[],
  selectedFilters: ListValueProp[]
): ListValueProp[] => {
  const results = defaultGroups
    .map((group) => ({
      id: group.id,
      label: group.name,
      selected: isSelected(group, selectedFilters),
    }))
    .concat(
      adminGroups.map((group) => ({
        id: group.id,
        label: group.name,
        selected: isSelected(group, selectedFilters),
      }))
    );
  return sortBy(results, [(group) => group.label.toLowerCase()]);
};

const IncidentTypeTableControls: React.FC<TableControlProps> = ({
  viewOption = "all",
  selectedFilters,
  disableViewFilter = false,
  onSearchKeyChange,
  onViewChange,
  onGroupFilterChange,
}) => {
  const { t } = useTranslation("common");
  const { t: incidentAddEditT } = useTranslation("incidentAddEdit");
  const classes = useStyles();
  const viewOptions = [
    {
      id: "all",
      label: incidentAddEditT("members.controls.view.all"),
      selected: viewOption !== "member",
    },
    {
      id: "member",
      label: incidentAddEditT("members.controls.view.member"),
      selected: viewOption === "member",
    },
  ];

  const { data: defaultGroups } = useGroups(
    {
      shouldRetryOnError: false,
    },
    {
      groupType: "Default",
      pageSize: 1000,
    }
  );

  const { data: adminGroups } = useGroups(
    {
      shouldRetryOnError: false,
    },
    {
      groupType: "Admin",
    }
  );
  const groupValues = generateGroupValues(
    defaultGroups?.data || [],
    adminGroups?.data || [],
    selectedFilters || []
  );

  return (
    <HvGrid className={classes.root} item xs={12}>
      <div className={classes.inputPlaceholder}>
        <HvInput
          className={classes.leftContainer}
          aria-label={t("common:search.ariaLabel")}
          placeholder={t("common:search.placeholder")}
          type="search"
          onEnter={(event, value) => {
            event.preventDefault();
            onSearchKeyChange?.(value);
          }}
        />
      </div>
      <div className={classes.rightContainer}>
        <HvDropdown
          className={classes.viewFilter}
          label={incidentAddEditT("members.controls.view.label")}
          aria-label={incidentAddEditT("members.controls.view.label")}
          onChange={(item) => {
            onViewChange?.(item as ListValueProp);
          }}
          values={viewOptions}
          disabled={disableViewFilter}
        />
        <HvDropdown
          className={classes.groupFilter}
          {...(groupValues.length > 10 && {
            classes: {
              dropdownListContainer: classes.filterDropdownListContainer,
            },
          })}
          multiSelect
          showSearch={groupValues.length > 10}
          label={incidentAddEditT("members.controls.group.label")}
          labels={{
            selectAll: incidentAddEditT("members.controls.group.all"),
          }}
          placeholder={incidentAddEditT("members.controls.group.placeholder")}
          aria-label={incidentAddEditT("members.controls.group.label")}
          onChange={(item) => {
            onGroupFilterChange?.(item as ListValueProp[]);
          }}
          values={groupValues}
        />
      </div>
    </HvGrid>
  );
};

export default IncidentTypeTableControls;
